import { Icon } from "@mdi/react";
import { useCallback, useRef } from "react";

import { mdiRefresh } from "@mdi/js";
import { Input, Tooltip } from "antd";

import { useIpdbStore } from "../../contexts/IpdbStoreProvider";
import {
  selectBinErrorMsg,
  selectSelectedIpdbFieldData
} from "../../contexts/ipdbSelectors";
import { type IpdbState, type SelectedIpdbFieldData } from "../../contexts/ipdbStore";
import {
  BinContainer,
  BinInputLabel,
  BinItem,
  BinResetButton,
  IpdbOptionItem,
  StyledAlert
} from "./shared/IpdbStyledComponents";

interface IpdbBinSettingsProps {
  binSettingDebounce: (data: SelectedIpdbFieldData) => void;
  onBinSettingChange: (data: SelectedIpdbFieldData) => void;
}

export default function IpdbBinSettings({
  binSettingDebounce,
  onBinSettingChange
}: IpdbBinSettingsProps) {
  const selectedIpdbFieldData = useIpdbStore(selectSelectedIpdbFieldData);
  const binErrorMsg = useIpdbStore(selectBinErrorMsg);
  const updateIpdbState = useIpdbStore((s) => s.update);

  // Ref tracks latest field data so handleEnter always has the current value
  const fieldDataRef = useRef(selectedIpdbFieldData);
  fieldDataRef.current = selectedIpdbFieldData;

  const setField = useCallback(
    (payload: Partial<IpdbState>) => updateIpdbState(payload),
    [updateIpdbState]
  );

  const updateBinField = (field: string, inputValue: string) => {
    const updatedData: SelectedIpdbFieldData = {
      ...selectedIpdbFieldData,
      ipdbBin: {
        ...(selectedIpdbFieldData.ipdbBin ?? {
          lessThan: "",
          binSize: "",
          greaterThan: ""
        }),
        [field]: inputValue
      }
    };
    setField({
      isBinUsingDefaultValues: false,
      selectedIpdbFieldData: updatedData
    });
    binSettingDebounce(updatedData);
  };

  const handleEnter = () => {
    setField({ isBinUsingDefaultValues: false });
    onBinSettingChange(fieldDataRef.current);
  };

  return (
    <>
      <IpdbOptionItem className="ipdb-option-item">
        <label className="ipdb-option-bin-label">Bin</label>
        <BinContainer>
          <Tooltip title="Reset Default">
            <BinResetButton
              type="link"
              data-testid="bin-reset-button"
              onClick={() => setField({ isBinUsingDefaultValues: true })}>
              <Icon path={mdiRefresh} size={"18px"} />
            </BinResetButton>
          </Tooltip>

          <BinItem>
            <BinInputLabel>Less Than</BinInputLabel>
            <Input
              type="number"
              data-testid="less-than-input"
              onChange={(evt) => updateBinField("lessThan", evt.target.value)}
              value={selectedIpdbFieldData.ipdbBin?.lessThan}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleEnter();
              }}
            />
          </BinItem>
          <BinItem>
            <BinInputLabel>Bin Size</BinInputLabel>
            <Input
              data-testid="bin-size-input"
              type="number"
              min={0}
              onChange={(evt) => updateBinField("binSize", evt.target.value)}
              value={selectedIpdbFieldData.ipdbBin?.binSize}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleEnter();
              }}
            />
          </BinItem>
          <BinItem>
            <BinInputLabel>Greater Than</BinInputLabel>
            <Input
              data-testid="greater-than-input"
              type="number"
              value={selectedIpdbFieldData.ipdbBin?.greaterThan}
              onChange={(evt) => updateBinField("greaterThan", evt.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleEnter();
              }}
            />
          </BinItem>
        </BinContainer>
      </IpdbOptionItem>

      {binErrorMsg && <StyledAlert message={binErrorMsg} type="error" showIcon />}
    </>
  );
}
